import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { tabTitle } from './content/copy';
import { Home } from './pages/Home';
import { JoinWarp } from './components/JoinWarp';
import { usePageMotion } from './motion/usePageMotion';

const JOIN = '/join';
/* Checkout (and Stripe) loads only when someone heads for it, keeping the homepage light. */
const loadJoin = () => import('./pages/Join');
const Join = lazy(() => loadJoin().then((module) => ({ default: module.Join })));
const Terms = lazy(() => import('./pages/Terms').then((module) => ({ default: module.Terms })));

function HomeRoute({ onJoin }: { onJoin: (from: HTMLElement) => void }) {
  const root = useRef<HTMLDivElement>(null);
  usePageMotion(root);
  return (
    <div className="page page--home" ref={root}>
      <main tabIndex={-1}>
        <Home onJoin={onJoin} />
      </main>
    </div>
  );
}

export function App() {
  const curtain = useRef<HTMLDivElement>(null);
  const [path, setPath] = useState(location.pathname);
  const [warp, setWarp] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const back = () => setPath(location.pathname);
    addEventListener('popstate', back);
    return () => removeEventListener('popstate', back);
  }, []);
  const go = (to: string) => {
    history.pushState(null, '', to);
    scrollTo(0, 0);
    setPath(to);
  };
  /* The warp grows out of the very button that was pressed, so it gets the element itself. */
  const join = (from: HTMLElement) => {
    if (warp) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return go(JOIN);
    // Fetch the page and its hero while the transition plays, so it is ready when the scene opens.
    void loadJoin();
    new Image().src = '/images/join/hero.webp';
    setWarp(from);
  };
  useEffect(() => {
    document.title = tabTitle;
  }, []);
  useEffect(() => {
    const hold = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 2;
    const face = curtain.current?.querySelector('.curtain-face');
    const opening = gsap.timeline({
      onComplete: () => {
        if (curtain.current) curtain.current.style.pointerEvents = 'none';
      },
    });
    if (face && hold) {
      opening
        .fromTo(
          face,
          { scale: 0, rotation: -20 },
          { scale: 1, rotation: 0, duration: 0.6, ease: 'back.out(1.8)' },
        )
        .to(face, { rotation: 8, duration: 0.25, ease: 'sine.inOut', yoyo: true, repeat: 3 })
        .to(face, { y: -12, duration: 0.3, ease: 'sine.inOut', yoyo: true, repeat: 1 }, '<')
        .to(face, { scale: 0.6, autoAlpha: 0, duration: 0.3, ease: 'back.in(1.6)' }, hold - 0.15);
    }
    opening.to(curtain.current, { autoAlpha: 0, duration: 0.5 }, hold);
    return () => {
      opening.kill();
    };
  }, []);
  return (
    <>
      {path === JOIN ? (
        <Suspense fallback={<main className="join-page" />}>
          <Join />
        </Suspense>
      ) : path === '/terms' ? (
        <Suspense fallback={<main className="join-page" />}>
          <Terms />
        </Suspense>
      ) : (
        <HomeRoute onJoin={join} />
      )}
      {warp && <JoinWarp from={warp} onReveal={() => go(JOIN)} onDone={() => setWarp(null)} />}
      <div ref={curtain} className="transition-curtain" aria-hidden="true">
        <img className="curtain-face" src="/images/loader-face.webp" alt="" />
      </div>
    </>
  );
}
